/**
 * The bar across the top: who you are, and on a wide screen where else to go.
 *
 * It used to carry the navigation twice — four links above md, the same four
 * in a drawer behind a hamburger below it — plus a theme control and the
 * account menu. Three things could be open at once and each had to know to
 * close the other two, which took two attempts to get right.
 *
 * The wordmark leads to Today. The account menu also has "Gardens on Grow"
 * (/gardens, the public listing) and "My public garden", the gardener's own
 * garden page (the address Profile > Public pages shows, read from GET
 * /gardens/mine only when the menu is opened, since that read gives a
 * gardener their address the first time); it is left out for a gardener with
 * no page address. Neither is a tab or a sixth link.
 *
 * "Photo sort" (the old Photos tab; D67 gave its place to What to plant) is the menu's first item, to /plants/photos.
 *
 * The account button carries a small hamburger badge on the avatar's lower
 * corner, so a gardener can tell the photo is a menu (B115, owner); the whole
 * is one button labelled "Menu" (nav.menu).
 *
 * On a phone the navigation now lives in TabBar along the bottom, so the
 * drawer, the hamburger and the state that coordinated them are gone. What is
 * left is the wordmark, the trial line and the avatar, and only the account
 * menu opens.
 *
 * The account menu is Radix rather than hand-rolled. It had role="menu" and
 * role="menuitem" on markup that offered none of what those promise — no arrow
 * keys, no focus moving into the menu, no focus returning to the trigger on
 * Escape. Announcing "menu" and then behaving like a div is worse than the
 * roleless version it replaced. Radix brings the whole contract, and takes the
 * outside-click and Escape handling with it.
 *
 * Until the garden has a postcode the destinations are not shown (nor is the
 * tab bar), nor is the trial line: Today asks for the ZIP first, the other
 * pages have nothing without it, and the days left are not the first thing
 * a new gardener should read.
 *
 * Under the row, on every page, Grow's frost or freeze warning when there is
 * one tonight or tomorrow night (AlertBar; owner, 2026-10-16: "a slim header
 * bar that shows on all pages").
 *
 * On Today (/today) neither the weather pill nor the trial line shows: Today
 * has the weather in its own card, and the days left are on every other
 * page's header and on the subscription page (owner, 2026-10-09). Their room
 * stays reserved (invisible, not removed) so the header reads the same on
 * every page and the navigation does not jump between Today and the others.
 * On the garden (/plants, and one plant's /plants/12) the pill is hidden the
 * same way: the weather bar at the top of Plants is Today's weather row
 * (owner, 2026-10-14), and the trial line stays.
 *
 * What it reads: the account (useAuth), the postcode's check
 * (useGardenPostcode), the gardens this person tends (GET /garden-members,
 * under GARDEN_PEOPLE_KEY, for the "Tending" switcher, which reloads the
 * app through gardenMembers.chooseGarden), the gallery standing
 * (useMyGallery, for the approvers' queue link), whether this is a Grow admin
 * (the account's `isAdmin`, through useIsGrowAdmin: no request; the gardener
 * list is read only on the view-as page) and, through TrialLine, the
 * billing status (features/billing useBilling). Mounted by Layout only.
 *
 * @module Header
 */

import { useEffect, useState } from 'react';
import { Menu } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { textVariants } from '@/shared/styles/variants';
import { cn } from '@/lib/utils';
import { useTheme } from '@/shared/hooks/useTheme';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { UserAvatar } from './UserAvatar';
import { isDestinationActive } from './isDestinationActive';
import { TrialLine } from '@/features/billing/components/SubscriptionWall';
import { useIsGrowAdmin } from '@/shared/api/adminUsers';
import { useMyGallery } from '@/features/gallery/lib/useMyGallery';
import { useQuery } from '@tanstack/react-query';
import { chooseGarden, chosenGarden, getGardenPeople } from '@/shared/api/gardenMembers';
import { GARDEN_PEOPLE_KEY } from '@/user/components/profileQueries';
import { canReportProblem } from '@/shared/lib/sentry';
import { openReportProblem } from '@/shared/lib/reportProblem';
import { WeatherPill } from './WeatherPill';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { getMyGarden } from '@/shared/api/gardens';
import { MY_GARDEN_QUERY_KEY, MY_GARDEN_STALE_MS } from '@/config/tunables/garden';
import { AlertBar } from './AlertBar';

/** The places worth a permanent link. */
const DESTINATIONS = [
  { to: '/today', key: 'nav.today' },
  { to: '/plants', key: 'nav.plants' },
  { to: '/beds', key: 'nav.locations' },
  { to: '/what-to-plant', key: 'nav.whatToPlant' },
  { to: '/season', key: 'nav.seasonsTab' },
];


/**
 * The bar across the top: the wordmark, the five destinations above md,
 * the trial line, the weather pill (neither on Today) and the account menu.
 *
 * @returns The header.
 */
export function Header(): JSX.Element {
  const { currentUser, mysqlUser, logout } = useAuth();
  const { usable: postcodeUsable } = useGardenPostcode();
  const { data: gallery } = useMyGallery();
  // Whether this is a Grow admin, the account answer's own word (isAdmin): "View as a gardener" (E16). No request (W24 Job C).
  const isGrowAdmin = useIsGrowAdmin();
  const { data: people } = useQuery({
    queryKey: GARDEN_PEOPLE_KEY,
    queryFn: getGardenPeople,
    enabled: Boolean(mysqlUser),
    staleTime: QUERY_STALE_TIME_MS,
  });
  const here = chosenGarden();
  const { reactTranslate: t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // Read for their updates only: the header redraws when the theme or the
  // preferences change, so its classes and words follow at once.
  useTheme();
  useUserConfig();

  const [accountOpen, setAccountOpen] = useState(false);

  // The gardener's own public garden, asked only when the account menu opens.
  const { data: myGarden } = useQuery({
    queryKey: [MY_GARDEN_QUERY_KEY],
    queryFn: getMyGarden,
    enabled: accountOpen && Boolean(mysqlUser),
    staleTime: MY_GARDEN_STALE_MS,
  });

  // Today shows the weather once, in its own card, and leaves the days left
  // to the other pages (2026-10-09): the pill and the trial line are hidden there.
  const onToday = location.pathname === '/today';
  // The garden shows the same weather row at its top (2026-10-14): the pill alone is hidden there.
  const onGarden = /^\/plants(\/\d+)?$/.test(location.pathname);

  // Arriving somewhere closes what got you there.
  useEffect(() => { setAccountOpen(false); }, [location.pathname]);

  /** Closes the menu and signs out. */
  const handleLogout = async (): Promise<void> => {
    setAccountOpen(false);
    await logout();
    // To "/": signed out, that is the landing page, so a reload (or pull-to-refresh)
    // shows the landing page again instead of the sign-in form for /today.
    navigate('/', { replace: true });
  };

  /** A link that says so when you are already on it: a filled pill in the theme's "where you are" colour. */
  const navClass = (isActive: boolean): string => cn(
    textVariants({ variant: 'nav', size: 'lg', font: 'serif' }),
    'rounded-sm px-1 transition-colors',
    // The pill's own ink replaces the nav's, hover included.
    isActive && 'bg-active px-3 py-1 text-active-foreground hover:text-active-foreground',
  );

  const displayName = mysqlUser?.display_name || currentUser?.displayName || currentUser?.email;

  return (
    <header
      className="sticky w-full top-0 z-40 bg-card grow:border-b fieldnotes:border-b border-border shadow-md"
    >
      <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-3 max-[399px]:gap-2 max-[399px]:px-3">
        {/* The wordmark: Playfair, whatever the theme. Type is the logo. */}
        <Link
          to="/today"
          className={cn(
            textVariants({ size: 'xl', weight: 'bold', variant: 'brand' }),
            'shrink-0 font-grow text-2xl leading-none',
          )}
        >
          Grow
        </Link>

        {/* Hidden until the garden has a postcode, as the tab bar is: Today asks for it. */}
        {postcodeUsable && (
        <nav aria-label={t('nav.primary')} className="hidden items-center gap-6 md:flex">
          {DESTINATIONS.map((destination) => (
            /* Each destination lights on its own path (isDestinationActive), as in the tab bar. */
            <Link
              key={destination.to}
              to={destination.to}
              aria-current={isDestinationActive(location.pathname, destination.to) ? 'page' : undefined}
              className={navClass(isDestinationActive(location.pathname, destination.to))}
            >
              {t(destination.key)}
            </Link>
          ))}
        </nav>
        )}

        <div className="flex min-w-0 items-center gap-2">
          {/* The trial line and the weather pill, as on every page. On Today the
              weather pill is not shown (the weather card says it) but keeps its
              room (invisible, which also takes it out of the tab order and the
              screen reader), so the navigation does not shift sideways when
              moving between Today and the other pages. The trial line, with a
              founding gardener's "free until" date, stays on Today as on the
              other pages (founder walk 2026-10-23, finding 12). `contents`
              keeps the wrapper out of the row's gaps and sizing. On a phone the line
              may shrink and wrap (min-w-0, the group not shrink-0), so a longer
              line, as the Spanish one, never pushes the profile circle off the
              screen (B71, finding 5). */}
          <div className="contents">
            {/* Days left, or that they have run out. Never shown to a subscriber,
                nor before the ZIP code is given (the welcome's first step).
                On a phone, the days alone in this row: it used to be a second
                row of its own, making the header two rows tall on every page. */}
            {postcodeUsable && (
              <>
                <TrialLine className="hidden sm:inline-flex" />
                <TrialLine compact className="min-w-0 sm:hidden" />
              </>
            )}

            {/* Today's sky and temperature, a button to the Weather page, on
                every tab. It left the tab bar when Seasons took its slot. On
                Today and the garden its room is kept and it is not shown
                (invisible: out of the tab order too): the weather card or bar
                there says it. */}
            <div className={cn('contents', onToday && 'invisible')} aria-hidden={onToday || undefined}>
              <WeatherPill className={cn(onGarden && 'invisible')} />
            </div>
          </div>

          <DropdownMenu open={accountOpen} onOpenChange={setAccountOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('nav.menu')}
                className="flex shrink-0 items-center gap-2 rounded-full p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {/* The avatar with a small hamburger on its lower corner: the sign that this opens the menu (B115). */}
                <span className="relative inline-flex shrink-0">
                  <UserAvatar mysqlUser={mysqlUser} firebaseUser={currentUser} className="h-12 w-12" />
                  <span
                    data-menu-sign=""
                    aria-hidden="true"
                    className="absolute -bottom-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm"
                  >
                    <Menu className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                </span>
                {/* aria-hidden so the accessible name stays the trigger's own
                    label: a visible name that is not part of the announced name
                    is the label-in-name failure, and the name here is decorative
                    beside the avatar. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    'hidden max-w-[10rem] truncate lg:inline',
                    textVariants({ variant: 'nav', font: 'serif' }),
                  )}
                >
                  {displayName}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Photo sort (the old Photos tab, D67): first in the menu; its page keeps its address and title. */}
              <DropdownMenuItem asChild>
                <Link to="/plants/photos">{t('nav.photoSort')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/profile">{t('nav.profile')}</Link>
              </DropdownMenuItem>
              {/* Only for a gardener with a page address, as Profile > Public pages. */}
              {myGarden?.slug && (
                <DropdownMenuItem asChild>
                  <Link to={`/@${myGarden.slug}`}>{t('nav.myPublicGarden')}</Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to="/gardens">{t('nav.gardensOnGrow')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/subscribe">{t('nav.subscription')}</Link>
              </DropdownMenuItem>
              {/* Only where there is a choice to make. One garden needs no
                  switcher, and a menu item that changes nothing is noise. */}
              {(people?.gardens.length ?? 0) > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    {t('nav.tending')}
                  </DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => chooseGarden(null)}>
                    {here === null ? `✓ ${t('nav.myGarden')}` : t('nav.myGarden')}
                  </DropdownMenuItem>
                  {people?.gardens.map((garden) => (
                    <DropdownMenuItem key={garden.memberId} onSelect={() => chooseGarden(garden.ownerId)}>
                      {here === garden.ownerId ? '✓ ' : ''}{t('nav.gardenOf', { name: garden.ownerName })}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
              {gallery?.approver && (
                <DropdownMenuItem asChild>
                  <Link to="/admin/gallery">{t('nav.galleryQueue')}</Link>
                </DropdownMenuItem>
              )}
              {isGrowAdmin && (
                <DropdownMenuItem asChild>
                  <Link to="/admin/view-as">{t('nav.viewAs')}</Link>
                </DropdownMenuItem>
              )}
              {/* Only when there is somewhere for it to go. A menu item that
                  opens nothing is worse than none. */}
              {canReportProblem() && (
                <DropdownMenuItem onSelect={() => { openReportProblem(); }}>
                  {t('nav.reportProblem')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => { void handleLogout(); }}>
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

        </div>
      </div>

      {/* A frost or freeze tonight or tomorrow night: a slim bar under the row, on every page (AlertBar). */}
      {postcodeUsable && <AlertBar />}
    </header>
  );
}
